import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { TranslateModule } from '@ngx-translate/core';
import { ModalComponent } from '../../../utils/modal/modal.component';
import { FormModalComponent } from '../../../utils/form-modal/form-modal.component';
import { AdminUsersService } from '../../../../services/admin/admin-users.service';
import { UserModel } from '../../../../models/resources/user.model';
import { Role } from '../../../../enums/role';
import { FormInputComponent } from '../../../utils/form-input/form-input.component';
import { CheckboxInputComponent } from '../../../utils/checkbox-input/checkbox-input.component';
import { ValidationErrorComponent } from '../../../utils/validation-error/validation-error.component';
import { EnumSelectComponent } from '../../../utils/enum-select/enum-select.component';
import { castRoleEnum } from '../../../../enums/role';

@Component({
  selector: 'app-create-user-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FormModalComponent,
    FormInputComponent,
    CheckboxInputComponent,
    ValidationErrorComponent,
    EnumSelectComponent,
  ],
  templateUrl: './create-user-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateUserModalComponent implements OnInit {
  /** When set, modal is in edit mode: form prefilled and save calls update. */
  user: UserModel | null = null;

  loading = false;
  errorMessage: string | null = null;
  validationErrors: Record<string, string[]> = {};

  /** Returns validation errors for a field, or empty array if none. Use in template to satisfy strict null checks. */
  getErrors(key: string): string[] {
    return this.validationErrors[key] ?? [];
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  form = this.fb.group({
    username: this.fb.control('', { validators: [Validators.required], nonNullable: true }),
    email: this.fb.control<string | null>(null),
    role: this.fb.control<Role>(Role.USER, { validators: [Validators.required], nonNullable: true }),
    active: this.fb.control<boolean>(true, { nonNullable: true }),
  });

  get isEditMode(): boolean {
    return this.user != null && this.user.id != null;
  }

  readonly roleOptions = Role;
  readonly roleLabelKey = castRoleEnum;

  constructor(
    public modalRef: MdbModalRef<CreateUserModalComponent>,
    private fb: FormBuilder,
    private usersService: AdminUsersService,
  ) {}

  ngOnInit(): void {
    if (this.user) {
      this.form.patchValue({
        username: this.user.username ?? '',
        email: this.user.email ?? null,
        role: this.user.role ?? Role.USER,
        active: this.user.active ?? true,
      });
    }
  }

  save(): void {
    if (this.form.invalid || this.loading) {
      return;
    }

    this.errorMessage = null;
    this.validationErrors = {};
    this.loading = true;

    const payload = {
      username: this.form.value.username ?? '',
      email: this.form.value.email ?? null,
      role: this.form.value.role ?? Role.USER,
      active: this.form.value.active ?? true,
    };

    const request$ = this.isEditMode
      ? this.usersService.updateUser(this.user!.id, {
          ...payload,
          updatedAt: this.user!.updatedAt,
        })
      : this.usersService.createUser(payload);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user: UserModel) => {
          this.loading = false;
          this.modalRef.close(user);
        },
        error: (err: { status?: number; error?: { errors?: Record<string, string[]> } }) => {
          this.loading = false;
          if (err?.status === 422 && err?.error?.errors) {
            this.validationErrors = err.error.errors;
          }
          this.errorMessage = 'ADMIN.USERS.SAVE_FAILED';
          this.cdr.markForCheck();
        },
      });
  }

  discard(): void {
    this.modalRef.close();
  }
}
